/**
 * Azure's own verdict on its platform, for the subscriptions NOVA monitors.
 *
 * "No active issues" is a real answer and is rendered as one, with the time it
 * was established — a blank panel would be indistinguishable from a broken
 * fetch. Planned maintenance and advisories are shown separately from live
 * issues, because a scheduled Postgres reboot next week is not an incident now.
 */

import { CircleCheck, Icon, TriangleAlert } from "@norican/icons";
import { Alert, Badge, Card, Freshness, Timeline, Typography } from "@norican/react";

import type { CloudServiceHealth } from "@/lib/api";

import { EVENT_LABEL, EVENT_TONE, formatTimestamp } from "./status";

interface ServiceHealthBannerProps {
  health: CloudServiceHealth;
}

export function ServiceHealthBanner({ health }: ServiceHealthBannerProps) {
  const allClear = health.active_issues === 0;
  const other = health.events.filter((event) => event.event_type !== "ServiceIssue");
  const issues = health.events.filter((event) => event.event_type === "ServiceIssue");

  return (
    <Card variant="outline" accent="left" accentTone={allClear ? "success" : "danger"}>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Icon
              icon={allClear ? CircleCheck : TriangleAlert}
              size="md"
              tone={allClear ? "success" : "danger"}
            />
            <Typography variant="heading-4" as="h2">
              Azure Service Health
            </Typography>
          </div>
          {health.checked_at && (
            <Freshness label="Azure checked" since={health.checked_at} staleAfter={10 * 60_000} />
          )}
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          <Typography variant="body-sm" tone="muted">
            {health.status_detail}
          </Typography>

          {health.emerging_issues.length > 0 && (
            <div className="flex flex-col gap-2">
              {health.emerging_issues.map((issue) => (
                <Alert key={`${issue.kind}-${issue.title}`} tone="warning" title={issue.title}>
                  {issue.description ?? "Azure has published a tenant-wide status banner."}
                </Alert>
              ))}
            </div>
          )}

          {issues.length > 0 && (
            <div className="flex flex-col gap-2">
              {issues.map((event) => (
                <Alert key={event.id} tone="danger" title={event.title}>
                  <span className="flex flex-col gap-1">
                    <span>{event.summary ?? "Azure has not published a summary yet."}</span>
                    <span className="text-fg-muted text-xs">
                      {event.subscription_name}
                      {event.impacted_regions.length > 0 &&
                        ` · ${event.impacted_regions.join(", ")}`}
                      {` · updated ${formatTimestamp(event.last_update_at)}`}
                    </span>
                  </span>
                </Alert>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Badge
              tone={health.active_issues > 0 ? "danger" : "success"}
              variant="subtle"
              size="sm"
            >
              {health.active_issues} active issue{health.active_issues === 1 ? "" : "s"}
            </Badge>
            <Badge tone="neutral" variant="subtle" size="sm">
              {health.planned_maintenance} planned maintenance
            </Badge>
            <Badge tone="warning" variant="subtle" size="sm">
              {health.health_advisories} health advisor
              {health.health_advisories === 1 ? "y" : "ies"}
            </Badge>
            <Badge tone="neutral" variant="subtle" size="sm">
              {health.subscriptions_checked} subscription
              {health.subscriptions_checked === 1 ? "" : "s"} checked
            </Badge>
          </div>

          {health.subscriptions_failed.length > 0 && (
            <Alert tone="warning" title="Some subscriptions could not be checked">
              {health.subscriptions_failed.map((problem) => problem.name).join(", ")} refused the
              Service Health call, so the all-clear above does not cover{" "}
              {health.subscriptions_failed.length === 1 ? "it" : "them"}.
            </Alert>
          )}

          {other.length > 0 && (
            <div className="flex flex-col gap-2">
              <Typography variant="label" as="h3">
                Maintenance and advisories
              </Typography>
              <Timeline
                items={other.slice(0, 8).map((event) => ({
                  id: event.id,
                  title: event.title,
                  timestamp: `${EVENT_LABEL[event.event_type] ?? event.event_type} · ${formatTimestamp(
                    event.last_update_at,
                  )}`,
                  tone: EVENT_TONE[event.event_type] === "danger" ? "danger" : "warning",
                  description: `${event.subscription_name}${
                    event.impacted_services.length > 0
                      ? ` · ${event.impacted_services.join(", ")}`
                      : ""
                  }`,
                }))}
              />
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
